import { clsx } from 'clsx'
import { useLayoutVariant } from '../../hooks/useLayoutVariant'

export default function StatsCard({ title, value, subtitle, icon, color = 'blue', trend = null, className = '' }) {
  const { isAdminAppUI } = useLayoutVariant()
  const colorClasses = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
    green: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400',
    yellow: 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400',
    orange: 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400',
    red: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400',
    purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
    indigo: 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400'
  }

  const iconBgClasses = {
    blue: 'bg-blue-100 dark:bg-blue-900/40',
    green: 'bg-green-100 dark:bg-green-900/40',
    yellow: 'bg-yellow-100 dark:bg-yellow-900/40',
    orange: 'bg-orange-100 dark:bg-orange-900/40',
    red: 'bg-red-100 dark:bg-red-900/40',
    purple: 'bg-purple-100 dark:bg-purple-900/40',
    indigo: 'bg-indigo-100 dark:bg-indigo-900/40'
  }

  return (
    <div className={clsx(
      isAdminAppUI
        ? 'bg-[#1a2535] border border-[#243246] rounded-[14px] p-4 sm:p-[18px] group relative overflow-hidden'
        : 'card card-hover p-4 sm:p-6 group relative overflow-hidden bg-white/95',
      className
    )}>
      {/* Efecto de brillo sutil en hover */}
      <div className={clsx(
        'absolute inset-0 transition-all duration-500 pointer-events-none',
        isAdminAppUI
          ? 'bg-[rgba(0,200,150,0)] group-hover:bg-[rgba(0,200,150,0.04)]'
          : 'bg-gradient-to-br from-pastel-cream/0 to-primary-100/0 dark:from-primary-900/0 dark:to-primary-800/0 group-hover:from-pastel-cream/60 group-hover:to-primary-100/30 dark:group-hover:from-primary-900/20 dark:group-hover:to-primary-800/10'
      )} />
      
      <div className="flex items-center relative z-10">
        <div className="flex-shrink-0">
          {icon && (
            <div className={clsx(
              isAdminAppUI
                ? 'w-9 h-9 rounded-[10px] flex items-center justify-center transition-transform duration-300'
                : 'w-10 h-10 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300',
              iconBgClasses[color]
            )}>
              <div className={clsx(
                colorClasses[color],
                !isAdminAppUI && 'group-hover:scale-110 transition-transform duration-300'
              )}>
                {icon}
              </div>
            </div>
          )}
        </div>
        <div className="ml-3 sm:ml-4 w-0 flex-1 min-w-0">
          <dl>
            <dt className={clsx(
              'text-xs sm:text-sm font-medium truncate',
              isAdminAppUI ? 'text-[#7a8fa6]' : 'text-slate-500 dark:text-gray-400'
            )}>
              {title}
            </dt>
            <dd className="flex items-baseline justify-between mt-1">
              <div className="flex items-baseline flex-wrap gap-1 sm:gap-2">
                <div className={clsx(
                  'text-xl sm:text-2xl font-bold',
                  isAdminAppUI
                    ? 'text-[#e8edf5]'
                    : 'text-pastel-ink dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-300'
                )}>
                  {value}
                </div>
                {trend && (
                  <span className={clsx(
                    'ml-1 sm:ml-2 text-xs sm:text-sm font-medium flex items-center',
                    isAdminAppUI
                      ? 'text-[#66bb6a]'
                      : 'text-green-700 dark:text-green-400 animate-bounce-subtle'
                  )}>
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 sm:mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L6.707 7.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                    {trend}
                  </span>
                )}
              </div>
            </dd>
            {subtitle && (
              <dd className={clsx(
                'text-xs mt-1',
                isAdminAppUI ? 'text-[#7a8fa6]' : 'text-slate-500 dark:text-gray-400'
              )}>
                {subtitle}
              </dd>
            )}
          </dl>
        </div>
      </div>
    </div>
  )
}

export function StatsGrid({ children, className = '' }) {
  const { isAdminAppUI } = useLayoutVariant()
  return (
    <div className={clsx(
      isAdminAppUI ? 'grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4' : 'grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4',
      className
    )}>
      {children}
    </div>
  )
}
